import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserGroupIcon,
  CurrencyDollarIcon, 
  ShoppingBagIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  DocumentTextIcon,
  BanknotesIcon,
  DevicePhoneMobileIcon,
  ArrowDownTrayIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { clsx } from 'clsx';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Input from '../../components/common/Input';
import Modal from '../../components/common/Modal';
import { useAuth } from '../../hooks/useAuth';
import { useAgent } from '../../hooks/useAgent';
import { formatAgentData } from '../../services/agentAPI';
import agentAPI from '../../services/agentAPI';
import { ordersAPI } from '../../services/api';
import WeeklySpendChart from '../../components/dashboard/WeeklySpendChart';

interface SalesDashboardSummary {
  totals: {
    totalOrders: number;
    totalSpent: number;
  };
  thisWeek: {
    orders: number;
    spent: number;
    startDate: string;
    endDate: string;
    dailySpent: Array<{ day: string; spent: number; orders: number }>;
  };
}

const AgentDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');

  const [salesSummary, setSalesSummary] = useState<SalesDashboardSummary>({
    totals: { totalOrders: 0, totalSpent: 0 },
    thisWeek: { orders: 0, spent: 0, startDate: '', endDate: '', dailySpent: [] },
  });
  
  // Modal states
  const [showReportModal, setShowReportModal] = useState(false);
  
  // Form states
  const [submitting, setSubmitting] = useState(false);
  
  // Report form
  const [reportForm, setReportForm] = useState({
    startDate: '',
    endDate: '',
    format: 'pdf' as 'pdf' | 'csv' | 'excel'
  });
  
  // Use the custom agent hook
  const {
    stats,
    topProducts,
    loading,
    statsLoading,
    ordersLoading,
    productsLoading,
    searchOrders,
    timeRange,
    setTimeRange
  } = useAgent({
    autoLoadStats: true,
    autoLoadOrders: true,
    timeRange: '30d'
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <XCircleIcon className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const filteredOrders = searchOrders(searchTerm);

  useEffect(() => {
    let mounted = true;

    const loadSalesSummary = async () => {
      try {
        const response = await ordersAPI.getMySalesDashboard();
        const payload = response?.data as any;
        if (!mounted) return;
        if (payload?.success && payload?.data) {
          setSalesSummary(payload.data);
        }
      } catch {
        if (!mounted) return;
        setSalesSummary({
          totals: { totalOrders: 0, totalSpent: 0 },
          thisWeek: { orders: 0, spent: 0, startDate: '', endDate: '', dailySpent: [] },
        });
      }
    };

    loadSalesSummary();
    return () => {
      mounted = false;
    };
  }, [timeRange]);

  // Handle time range change
  const handleTimeRangeChange = (range: string) => {
    setTimeRange(range);
  };

  // Handle quick actions
  const handleNewOrder = () => {
    navigate('/packages');
  };

  const handleTopupWallet = () => {
    navigate('/wallet');
  };

  const handleGenerateReport = () => {
    setShowReportModal(true);
  };
  
  
  // Generate and download report
  const handleSubmitReport = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reportForm.startDate || !reportForm.endDate) {
      toast.error('Please select date range');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await agentAPI.generateReport({
        startDate: reportForm.startDate,
        endDate: reportForm.endDate,
        format: reportForm.format
      });

      const payload: any = response?.data;
      if (!payload?.success || !payload?.data) {
        throw new Error(payload?.message || 'Failed to generate report');
      }

      const report = payload.data;
      const filenameBase = `agent-report-${reportForm.startDate}-to-${reportForm.endDate}`;

      const downloadBlob = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      };

      if (reportForm.format === 'pdf') {
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          toast.error('Please allow popups to export PDF');
          return;
        }

        const esc = (v: any) => String(v ?? '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        const ordersByStatusHtml = Object.entries(report.ordersByStatus || {})
          .map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`)
          .join('');

        const revenueByNetworkHtml = Object.entries(report.revenueByNetwork || {})
          .map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`)
          .join('');

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${esc(filenameBase)}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 24px; }
              h1 { margin: 0 0 6px 0; }
              .muted { color: #555; font-size: 12px; }
              .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-top: 18px; }
              .card { border: 1px solid #ddd; border-radius: 10px; padding: 14px; }
              table { width: 100%; border-collapse: collapse; margin-top: 10px; }
              th, td { border: 1px solid #eee; padding: 8px; font-size: 12px; text-align: left; }
              th { background: #fafafa; }
            </style>
          </head>
          <body>
            <h1>Agent Report</h1>
            <div class="muted">Period: ${esc(reportForm.startDate)} to ${esc(reportForm.endDate)}</div>
            <div class="muted">Generated: ${esc(new Date().toLocaleString())}</div>

            <div class="grid">
              <div class="card">
                <div><strong>Total Sales</strong>: ${esc(report.totalSales)}</div>
                <div><strong>Total Revenue</strong>: ${esc(report.totalRevenue)}</div>
                <div><strong>Total Commission</strong>: ${esc(report.totalCommission)}</div>
                <div><strong>Customer Growth</strong>: ${esc(report.customerGrowth)}</div>
                <div><strong>Average Order Value</strong>: ${esc(report.averageOrderValue)}</div>
              </div>
              <div class="card">
                <div><strong>Orders By Status</strong></div>
                <table>
                  <thead><tr><th>Status</th><th>Count</th></tr></thead>
                  <tbody>${ordersByStatusHtml || '<tr><td colspan="2">—</td></tr>'}</tbody>
                </table>
              </div>
            </div>

            <div class="card" style="margin-top:16px;">
              <div><strong>Revenue By Network</strong></div>
              <table>
                <thead><tr><th>Network</th><th>Revenue</th></tr></thead>
                <tbody>${revenueByNetworkHtml || '<tr><td colspan="2">—</td></tr>'}</tbody>
              </table>
            </div>
          </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 250);
      } else {
        const rows: Array<[string, any]> = [
          ['startDate', reportForm.startDate],
          ['endDate', reportForm.endDate],
          ['totalSales', report.totalSales],
          ['totalRevenue', report.totalRevenue],
          ['totalCommission', report.totalCommission],
          ['customerGrowth', report.customerGrowth],
          ['averageOrderValue', report.averageOrderValue]
        ];

        const csv = [
          'metric,value',
          ...rows.map(([k, v]) => `${JSON.stringify(k)},${JSON.stringify(v)}`)
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const ext = reportForm.format === 'excel' ? 'csv' : 'csv';
        downloadBlob(blob, `${filenameBase}.${ext}`);
      }

      toast.success('Report generated successfully!');
      setShowReportModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to generate report');
    } finally {
      setSubmitting(false);
    }
  };
  

  // Show loading skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="animate-pulse space-y-4 sm:space-y-6">
            <div className="h-6 sm:h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 sm:w-1/4"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-28 sm:h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              <div className="lg:col-span-2 h-80 sm:h-96 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              <div className="h-80 sm:h-96 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6">

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-3 sm:gap-4"
      >
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
            Agent Dashboard
          </h1>
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
            Welcome back, {user?.profile?.firstName || 'Agent'}!
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 overflow-x-auto">
            {(['1d', '7d', '30d', '365d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => handleTimeRangeChange(range)}
                className={clsx(
                  'px-2 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap',
                  timeRange === range
                    ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                )}
              >
                {range === '1d' ? '1D' : range === '7d' ? '7D' : range === '30d' ? '30D' : '1Y'}
              </button>
            ))}
          </div>
          
          <Button 
            variant="outline"
            size="sm"
            className="flex items-center space-x-2"
            onClick={handleGenerateReport}
            disabled={statsLoading}
          >
            <CalendarIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Export Report</span>
            <span className="sm:hidden">Export</span>
          </Button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-4"
      >
        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-3">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Total Spent
              </p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {formatCurrency(salesSummary.totals.totalSpent)}
              </p>
            </div>
            <div className="flex-shrink-0 p-2.5 sm:p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
              <BanknotesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-3">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Total Orders
              </p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {salesSummary.totals.totalOrders?.toLocaleString() || '0'}
              </p>
            </div>
            <div className="flex-shrink-0 p-2.5 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <ShoppingBagIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-3">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Spent This Week
              </p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {formatCurrency(salesSummary.thisWeek.spent)}
              </p>
            </div>
            <div className="flex-shrink-0 p-2.5 sm:p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <ArrowTrendingUpIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2"
        >
          <Card className="p-4 sm:p-6 overflow-visible">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                Daily Spent (Mon - Sun)
              </h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">This week</span>
            </div>
            <WeeklySpendChart data={salesSummary.thisWeek.dailySpent || []} />
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="space-y-4 sm:space-y-6"
        >
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-900/30 rounded-2xl border border-purple-200 dark:border-purple-800 overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-4 border-b border-purple-200 dark:border-purple-800">
              <BoltIcon className="h-5 w-5 text-purple-600" />
              <h2 className="font-semibold text-gray-900 dark:text-white">Agent Tools</h2>
            </div>
            <div className="p-4 space-y-2">
              <button
                onClick={() => navigate('/customers')}
                className="w-full py-2 px-3 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors text-left"
              >
                <UserGroupIcon className="h-4 w-4 inline mr-2" />
                Customer Management
              </button>
              <button
                onClick={() => navigate('/storefront')}
                className="w-full py-2 px-3 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors text-left"
              >
                <ShoppingBagIcon className="h-4 w-4 inline mr-2" />
                My Storefront
              </button>
              <button
                onClick={() => navigate('/withdrawals')}
                className="w-full py-2 px-3 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium transition-colors text-left"
              >
                <BanknotesIcon className="h-4 w-4 inline mr-2" />
                Withdraw Funds
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <details className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-5">
        <summary className="cursor-pointer select-none text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
          More details
        </summary>
        <div className="mt-4 space-y-6">

      {/* Stats Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
      >
        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-3">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Total Sales
              </p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {formatCurrency(stats?.totalRevenue || 0)}
              </p>
              <div className="flex items-center mt-2">
                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                  Selected period
                </span>
              </div>
            </div>
            <div className="flex-shrink-0 p-2.5 sm:p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <ShoppingBagIcon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-3">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Total Orders
              </p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {stats?.totalOrders?.toLocaleString?.() || stats?.totalOrders || '0'}
              </p>
              <div className="flex items-center mt-2">
                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                  All time
                </span>
              </div>
            </div>
            <div className="flex-shrink-0 p-2.5 sm:p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <DocumentTextIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-3">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Today's Sales
              </p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {formatCurrency(stats?.todaySales || 0)}
              </p>
              <div className="flex items-center mt-2">
                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                  Today
                </span>
              </div>
            </div>
            <div className="flex-shrink-0 p-2.5 sm:p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
              <CurrencyDollarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-3">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                This Week's Sales
              </p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {formatCurrency(stats?.thisWeekSales || 0)}
              </p>
              <div className="flex items-center mt-2">
                {((stats?.growthRate || 0) > 0) ? (
                  <ArrowTrendingUpIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-green-500 dark:text-green-400 mr-1" />
                ) : (
                  <ArrowTrendingDownIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-red-500 dark:text-red-400 mr-1" />
                )}
                <span className={clsx(
                  'text-xs sm:text-sm font-medium',
                  ((stats?.growthRate || 0) > 0) ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                )}>
                  {Math.abs(stats?.growthRate || 0)}%
                </span>
              </div>
            </div>
            <div className="flex-shrink-0 p-2.5 sm:p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
              <ArrowTrendingUpIcon className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-3">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                This Month's Sales
              </p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {formatCurrency(stats?.thisMonthSales || 0)}
              </p>
              <div className="flex items-center mt-2">
                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                  This month
                </span>
              </div>
            </div>
            <div className="flex-shrink-0 p-2.5 sm:p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
              <CalendarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1 pr-3">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                Commission
              </p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {formatCurrency(stats?.commissionEarned || 0)}
              </p>
            </div>
            <div className="flex-shrink-0 p-2.5 sm:p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
              <BanknotesIcon className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2"
        >
          <Card className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                Recent Orders
              </h2>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 sm:flex-initial">
                  <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full sm:w-auto pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 focus:border-transparent"
                  />
                </div>
                <Button variant="outline" size="sm" className="flex-shrink-0">
                  <FunnelIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {ordersLoading ? (
                <div className="space-y-3">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    </div>
                  ))}
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingBagIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">
                    {searchTerm ? 'No orders found matching your search' : 'No orders yet'}
                  </p>
                </div>
              ) : (
                filteredOrders.slice(0, 5).map((order, index) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * index }}
                    className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-start sm:items-center justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="flex-shrink-0 mt-0.5 sm:mt-0">
                          {getStatusIcon(order.status)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-white truncate">
                            {order.customer}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate mt-0.5">
                            {order.package}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {order.customerPhone}
                            </p>
                            <span className="text-gray-400 dark:text-gray-600">•</span>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDate(order.date)}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 flex-shrink-0">
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          <Badge 
                            variant={order.status === 'completed' ? 'success' : order.status === 'pending' ? 'warning' : order.status === 'processing' ? 'info' : 'danger'}
                            className="capitalize text-xs"
                          >
                            {order.status}
                          </Badge>
                          <Badge className={formatAgentData.formatNetworkColor(order.network) + ' text-xs'}>
                            {order.network.toUpperCase()}
                          </Badge>
                        </div>
                        <span className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white whitespace-nowrap">
                          {formatCurrency(order.amount)}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button 
                variant="ghost" 
                className="w-full"
                onClick={() => navigate('/orders')}
              >
                View All Orders
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* Top Products & Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="space-y-6"
        >
          {/* Top Products */}
          <Card className="p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
              Top Products
            </h2>
            
            <div className="space-y-4">
              {productsLoading ? (
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : topProducts.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 dark:text-gray-400 text-sm">
                    No product data available
                  </p>
                </div>
              ) : (
                topProducts.slice(0, 4).map((product, index) => (
                  <div key={index} className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                        {product.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <Badge className={formatAgentData.formatNetworkColor(product.network) + ' text-xs'}>
                          {product.network.toUpperCase()}
                        </Badge>
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          {product.sales} sales
                        </span>
                      </div>
                    </div>
                    
                    <div className="text-right flex-shrink-0">
                      <p className="font-semibold text-gray-900 dark:text-white text-sm whitespace-nowrap">
                        {formatCurrency(product.revenue)}
                      </p>
                      <div className="flex items-center justify-end mt-1">
                        {product.growth > 0 ? (
                          <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 dark:text-green-400 mr-0.5" />
                        ) : (
                          <ArrowTrendingDownIcon className="h-3 w-3 text-red-500 dark:text-red-400 mr-0.5" />
                        )}
                        <span className={clsx(
                          'text-xs font-medium',
                          product.growth > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                        )}>
                          {Math.abs(product.growth)}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Quick Actions */}
          <Card className="p-4 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6">
              Quick Actions
            </h2>
            
            <div className="space-y-3">
              <Button 
                className="w-full justify-start" 
                variant="primary"
                onClick={handleNewOrder}
              >
                <ShoppingBagIcon className="h-4 w-4 mr-2" />
                New Order
              </Button>
              
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={handleTopupWallet}
              >
                <DevicePhoneMobileIcon className="h-4 w-4 mr-2" />
                Top-up Wallet
              </Button>
              
              <Button 
                className="w-full justify-start" 
                variant="outline"
                onClick={handleGenerateReport}
              >
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
            </div>
          </Card>

          {/* Pending Orders Alert */}
          {(stats?.pendingOrders || 0) > 0 && (
            <Card className="p-4 border-2 border-yellow-200 dark:border-yellow-800/50 bg-yellow-50 dark:bg-yellow-900/10">
              <div className="flex items-start gap-3">
                <ClockIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm sm:text-base text-yellow-900 dark:text-yellow-100">
                    {(stats?.pendingOrders || 0)} Pending Orders
                  </p>
                  <p className="text-xs sm:text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    Orders need your attention
                  </p>
                  <Button variant="ghost" size="sm" className="mt-2 text-yellow-700 dark:text-yellow-300 hover:text-yellow-900 dark:hover:text-yellow-100 px-0">
                    Review Now
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </motion.div>
      </div>

        </div>
      </details>
      
      {/* Modals */}
      <AnimatePresence>
        {/* Generate Report Modal */}
        {showReportModal && (
          <Modal
            isOpen={showReportModal}
            onClose={() => setShowReportModal(false)}
            title="Generate Report"
          >
            <form onSubmit={handleSubmitReport} className="space-y-4">
              <Input
                label="Start Date"
                type="date"
                value={reportForm.startDate}
                onChange={(e) => setReportForm({ ...reportForm, startDate: e.target.value })}
                required
              />

              <Input
                label="End Date"
                type="date"
                value={reportForm.endDate}
                onChange={(e) => setReportForm({ ...reportForm, endDate: e.target.value })}
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Format
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(['pdf', 'csv', 'excel'] as const).map((format) => (
                    <button
                      key={format}
                      type="button"
                      onClick={() => setReportForm({ ...reportForm, format })}
                      className={clsx(
                        'px-4 py-2 rounded-lg border-2 font-medium transition-all uppercase',
                        reportForm.format === format
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      )}
                    >
                      {format}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowReportModal(false)}
                  className="flex-1"
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  className="flex-1"
                  disabled={submitting}
                >
                  <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                  {submitting ? 'Generating...' : 'Download Report'}
                </Button>
              </div>
            </form>
          </Modal>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default AgentDashboard;
